import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Grid,
    MenuItem,
    IconButton
} from '@mui/material';
import { useEffect, useState } from 'react';
import { IBillingDetail } from './models/Billing';
import NumberFormat from 'react-number-format';
import TextFieldRight from 'components/ui/TextFieldRight';
import { useRecoilValue } from 'recoil';
import { currentUserSessionAtom } from 'library/store';
import { useTranslation } from 'react-i18next';
import { Enum_ARTICLE_OPTION, Enum_CONSUMPTION_OPTION, IEnumerationItem } from 'features/configuration/models/Enumeration';


type Props = {
    open: boolean,
    detail: IBillingDetail | null,
    onClose: () => void,
    onSave: (detail: IBillingDetail) => void,

    enumItems?: IEnumerationItem[],
};

export function EditBillingDetailDialog({
    open,
    detail,
    onClose,
    onSave,

    enumItems
}: Props) {

    const { t, i18n } = useTranslation();
    const { language: lg, taskCodes, userDescription, userName,
        employeeId, roleEntities, applicationSetup, userStores } = useRecoilValue(currentUserSessionAtom);


    const [quantity, setQuantity] = useState<number>(detail?.quantity || 0);
    const [unityAmount, setUnityAmount] = useState<number>(detail?.unityAmount || 0);

    const [productOptionId, setProductOptionId] = useState<number>(detail?.productOptionId || 0);
    const [consumptionOption, setConsumptionOption] = useState<string>(detail?.consumptionOption || '');
    const [articleOption, setArticleOption] = useState<string>(detail?.articleOption || '');

    useEffect(() => {
        if (detail) {
            setQuantity(detail.quantity);
            setUnityAmount(detail.unityAmount);
        }
    }, [detail]);

    if (!detail) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{detail.productName}</DialogTitle>

            <DialogContent sx={{
                overflowX: 'hidden',
                px: 3,      // controlled padding
            }}>
                <Stack spacing={2} mt={1} flexDirection={'column'}>

                    {/* OPTIONS — ONE LINE */}
                    <Grid container spacing={1}>
                        {['article', 'consumption', 'packaging'].includes(detail.type) && <Grid item xs={6}>
                            <TextField
                                select
                                label={`${t('Product')} : ${t('Option')}`}
                                value={productOptionId}
                                onChange={(e) => setProductOptionId(+e.target.value)}
                                sx={{width:'calc(100% - 8px)'}}
                                size="small"
                            >
                                {(detail.productOptions || []).map(
                                    (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                                }
                            </TextField>
                        </Grid>}

                        {(detail.type === 'consumption') && <Grid item xs={6}>
                            <TextField
                                select
                                label={`${t('Consumption')} - ${t('Option')}`}
                                value={consumptionOption}
                                onChange={(e) => setConsumptionOption(e.target.value)}
                                sx={{width:'calc(100% - 8px)'}}
                                size="small"
                            >
                                {enumItems && enumItems.filter(e => e.enumerationCode === Enum_CONSUMPTION_OPTION &&
                                    e.parentEnumerationItemCode === detail.consumptionOptionClass).map(
                                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                                }
                            </TextField>
                        </Grid>}

                        {(detail.type === 'article') && <Grid item xs={6}>
                            <TextField
                                select
                                label={`${t('Article')} - ${t('Option')} `}
                                value={articleOption}
                                onChange={(e) => setArticleOption(e.target.value)}
                                sx={{width:'calc(100% - 8px)'}}
                                size="small"
                            >
                                {enumItems && enumItems.filter(e => e.enumerationCode === Enum_ARTICLE_OPTION &&
                                    e.parentEnumerationItemCode === detail.articleFilterOption).map(
                                        (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                                }
                            </TextField>
                        </Grid>}
                    </Grid>

                    {/* QUANTITY + PRICE — SECOND LINE */}
                    <Grid container spacing={1} alignItems="center">
                        {/* QUANTITY WITH +/- */}
                        <Grid item xs={6}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton
                                    size="small"
                                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                >
                                    −
                                </IconButton>

                                <NumberFormat
                                    label={t('Quantity')}
                                    value={quantity}
                                    thousandSeparator={lg.includes('fr') ? ' ' : ','}
                                    decimalScale={0}
                                    allowNegative={false}
                                    onValueChange={(v) => setQuantity(v.floatValue || 1)}
                                    customInput={TextFieldRight}
                                    size="small"
                                    sx={{width:'calc(100% - 8px)'}}
                                />

                                <IconButton
                                    size="small"
                                    onClick={() => setQuantity((q) => q + 1)}
                                >
                                    +
                                </IconButton>
                            </Stack>
                        </Grid>

                        {/* UNIT PRICE */}
                        <Grid item xs={6}>
                            <NumberFormat
                                label={t('Unity price')}
                                value={unityAmount}
                                thousandSeparator={lg.includes('fr') ? ' ' : ','}
                                decimalScale={2}
                                allowNegative={false}
                                onValueChange={(v) => setUnityAmount(v.floatValue || 0)}
                                customInput={TextFieldRight}
                                size="small"
                                sx={{width:'calc(100% - 8px)'}}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>

            {/* ACTIONS */}
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={() =>
                        onSave({
                            ...detail,
                            quantity,
                            unityAmount,
                            productOptionId,
                            consumptionOption,
                            articleOption
                        })
                    }
                >
                    {t('Confirm')}
                </Button>
            </DialogActions>
        </Dialog>

    );
}
